import React, { useEffect } from 'react';
import { Button, StatusBadge } from './ui';

/**
 * Slide-over panel showing one submission in full, with status controls.
 * Closes on Escape or backdrop click.
 */
const DetailDrawer = ({ open, onClose, title, subtitle, status, statuses, onStatusChange, footer, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Stop the page behind the drawer from scrolling.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={title}>
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl animate-[slideIn_0.2s_ease-out]">
        <style>{'@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}'}</style>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-0.5 truncate text-sm text-slate-500">{subtitle}</p>}
            {status && <div className="mt-2"><StatusBadge status={status} /></div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Status controls */}
        {statuses?.length > 0 && (
          <div className="border-b border-slate-100 bg-slate-50/60 px-6 py-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Change status</p>
            <div className="flex flex-wrap gap-2">
              {statuses.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onStatusChange(s)}
                  disabled={s === status}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition ${
                    s === status
                      ? 'cursor-default bg-slate-900 text-white'
                      : 'bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {s.replace(/-/g, ' ')}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export { Button };
export default DetailDrawer;
